import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface PreviewMeshData {
  triangles: Array<{
    v0: { x: number; y: number; z: number };
    v1: { x: number; y: number; z: number };
    v2: { x: number; y: number; z: number };
    normal: { x: number; y: number; z: number };
    groupId?: number;
    partCode?: string;
  }>;
  triangleCount: number;
  vertexCount: number;
  parts?: Array<{ groupId: number; name: string; label: string }>;
  analysis?: {
    skipped?: boolean;
    isWatertight: boolean;
    nonManifoldEdgeCount: number;
    nonManifoldEdges: Array<{
      groupId?: number;
      v0: { x: number; y: number; z: number };
      v1: { x: number; y: number; z: number };
      triangleCount: number;
    }>;
  };
}

export interface PartRenderInfo {
  colorKey: string;
  color: string;
  name: string;
}

/** Converts worker triangles into render geometry and owns incremental scene mesh updates. */
export class PreviewRenderer {
  constructor(private readonly assembly: THREE.Group) {}

  public clearGroups(groupIds: ReadonlySet<number>): void {
    const toRemove: THREE.Object3D[] = [];
    this.assembly.traverse((child) => {
      if (child !== this.assembly && child.userData?.groupId !== undefined && groupIds.has(child.userData.groupId)) {
        toRemove.push(child);
      }
    });
    for (const object of toRemove) {
      if ((object as THREE.Mesh).geometry) (object as THREE.Mesh).geometry.dispose();
      this.assembly.remove(object);
    }
  }

  public createPartMesh(
    meshData: PreviewMeshData,
    groupId: number,
    info: PartRenderInfo,
    internalLightOn: boolean,
  ): THREE.Mesh | null {
    const { geometry, faceCodes } = meshDataToGeometry(meshData, groupId);
    if (geometry.getAttribute('position').count === 0) {
      geometry.dispose();
      return null;
    }
    const material = new THREE.MeshPhongMaterial({
      color: Number.parseInt(info.color.replace('#', '0x'), 16),
      side: THREE.DoubleSide,
      shininess: 45,
      specular: 0x444444,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = internalLightOn;
    mesh.receiveShadow = internalLightOn;
    mesh.userData = {
      groupId,
      groupName: `group_${groupId}`,
      colorKey: info.colorKey,
      defaultMaterial: material,
      partName: info.name,
      faceCodes,
      partCode: meshData.parts?.find((part) => part.groupId === groupId)?.name,
      partLabel: meshData.parts?.find((part) => part.groupId === groupId)?.label,
    };
    this.assembly.add(mesh);
    return mesh;
  }
}

/** Builds BufferGeometry while preserving triangle-to-part-code indexing. */
export function meshDataToGeometry(
  meshData: PreviewMeshData,
  targetGroupId?: number,
): { geometry: THREE.BufferGeometry; faceCodes: string[] } {
  const geometry = new THREE.BufferGeometry();
  const vertices: number[] = [];
  const normals: number[] = [];
  const faceCodes: string[] = [];
  for (const triangle of meshData.triangles) {
    const groupId = triangle.groupId ?? 0;
    if (targetGroupId !== undefined && groupId !== targetGroupId) continue;
    vertices.push(
      triangle.v0.x, triangle.v0.y, triangle.v0.z,
      triangle.v1.x, triangle.v1.y, triangle.v1.z,
      triangle.v2.x, triangle.v2.y, triangle.v2.z,
    );
    for (let index = 0; index < 3; index++) {
      normals.push(triangle.normal.x, triangle.normal.y, triangle.normal.z);
    }
    faceCodes.push(triangle.partCode ?? '');
  }
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));
  const merged = meshData.triangles.length > 100_000 ? geometry : mergeVertices(geometry, 1e-4);
  merged.computeVertexNormals();

  const triangleCount = merged.getIndex()
    ? merged.getIndex()!.count / 3
    : merged.getAttribute('position').count / 3;
  if (triangleCount !== faceCodes.length) {
    console.warn(`[partCode] triangle count changed during merge (${triangleCount} vs ${faceCodes.length}); dropping part codes`);
    return { geometry: merged, faceCodes: [] };
  }
  return { geometry: merged, faceCodes };
}
